/**
 * Consent-gated analytics scaffold.
 *
 * Nothing runs until the visitor accepts the cookie banner. Once `accepted`,
 * we lazy-init whatever analytics providers are configured via env variables:
 *   - REACT_APP_GA_MEASUREMENT_ID  (Google Analytics 4)
 *   - REACT_APP_PLAUSIBLE_DOMAIN   (Plausible - privacy-friendly alternative)
 *
 * If neither env var is set, we simply record page views to a debug console
 * log — safe no-op for local dev. Zero PII collection until consent.
 */

const CONSENT_KEY = "wofy_cookie_consent";
let _initialized = false;
let _consentGiven = false;

function loadGA4(measurementId) {
  if (window.gtag || !measurementId) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { anonymize_ip: true, send_page_view: true });
  // eslint-disable-next-line no-console
  console.info("[analytics] GA4 initialized:", measurementId);
}

function loadPlausible(domain) {
  if (!domain) return;
  if (document.querySelector('script[data-plausible="true"]')) return;
  const s = document.createElement("script");
  s.defer = true;
  s.src = "https://plausible.io/js/script.js";
  s.setAttribute("data-domain", domain);
  s.setAttribute("data-plausible", "true");
  document.head.appendChild(s);
  // eslint-disable-next-line no-console
  console.info("[analytics] Plausible initialized:", domain);
}

function initProviders() {
  if (_initialized) return;
  const ga = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const plausible = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  if (ga) loadGA4(ga);
  if (plausible) loadPlausible(plausible);
  if (!ga && !plausible) {
    // eslint-disable-next-line no-console
    console.info("[analytics] Consent granted — no provider configured (dev/no-op).");
  }
  _initialized = true;
}

/** Public: install listeners exactly once, from App.js at boot. */
export function installAnalyticsConsentHook() {
  // Replay a previous decision saved to localStorage.
  try {
    const prev = localStorage.getItem(CONSENT_KEY);
    if (prev === "accepted") {
      _consentGiven = true;
      initProviders();
    }
  } catch { /* localStorage blocked */ }

  // Listen for future consent changes.
  window.addEventListener("wofy-cookie-consent", (e) => {
    const choice = e?.detail?.choice;
    if (choice === "accepted") {
      _consentGiven = true;
      initProviders();
    }
    // On "rejected" we intentionally do nothing — providers remain uninitialized.
  });
}

/** Public: safe to call from any component; drops the event when no consent. */
export function track(event, props = {}) {
  if (!_consentGiven) return;
  try {
    if (window.gtag) window.gtag("event", event, props);
    if (window.plausible) window.plausible(event, { props });
  } catch { /* silent */ }
}
